import React, { useState, useCallback, useEffect } from 'react';
import { useMatch, useNavigate, useParams, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { validateAddEditForm } from './validations';
import Header from './components/Header';
import Buttons from './components/Buttons';
import DivisionDetailsTab from './components/DivisionDetailsTab';
import useFetch from '../../../../hooks/useFetch';
import { VALIDATION_FIELD_MAPPING } from '../../../../utils/appConstant';
import { AddEdit as AddEditInterface, AddEditApi } from '../../../../interfaces/productDivision.interface';
import { Validation } from '../../../../interfaces/common';
import ProductDivisionService from '../../../../services/productDivision.service';
import { Modules, AuthRoutes, RouteParamsTypes } from '../../../../enums';
import Spinner from '../../../../components/Spinner';
import Toast from '../../../../components/Toast';

const initialFormData: AddEditInterface = {
  name: '',
  description: '',
  status: 'true',
};

const initialValidation: Validation = { validate: true, fields: [] };

const ProductDivisionAddEditView: React.FC = () => {
  const location = useLocation();
  const { t } = useTranslation();
  const { id } = useParams();
  const view = !!useMatch(Modules.products + AuthRoutes.productDivisions + RouteParamsTypes.view + '/:id');
  const edit = !!useMatch(Modules.products + AuthRoutes.productDivisions + RouteParamsTypes.edit + '/:id');
  const navigate = useNavigate();

  const [formData, setFormData] = useState<AddEditInterface>(initialFormData);
  const [loading, setLoading] = useState(false);
  const [actionTwoLoading, setActionTwoLoading] = useState<boolean>(false);
  const [validation, setValidation] = useState<Validation>(initialValidation);

  useEffect(() => {
    setFormData(initialFormData);
    setValidation(initialValidation);
  }, [location.key]);

  const { loading: getContentLoading } = useFetch({
    service: ProductDivisionService,
    func: 'get',
    payload: id,
    dependency: [id, location.key],
    skip: !id,
    onSuccess: async (response: any) => {
      setFormData({
        id,
        name: response.name,
        description: response.description,
        status: String(response.is_active),
      });
    },
    onError: (error: any) => {
      // handle not found if needed
    },
  });

  const handleInputChange = useCallback(
    (event: any, name: string) => {
      let value: any = event.target.value;
      setFormData((prev) => ({ ...prev, [name]: value }));
    },
    [setFormData]
  );

  const handleInputBlur = useCallback(
    (event: any, name: string) => {
      let value = event.target.value.trim();
      let newValidation = validateAddEditForm({ ...formData, [name]: value }, name);
      setValidation(newValidation);
    },
    [formData]
  );

  const handleSubmit = useCallback(
    async (action: '1' | '2', event?: React.FormEvent) => {
      event && event.preventDefault();

      const newValidation = validateAddEditForm(formData);
      setValidation(newValidation);
      if (!newValidation.validate) return;

      if (action === '1') setLoading(true);
      else setActionTwoLoading(true);

      try {
        const payload: AddEditApi = {
          name: formData.name.trim(),
          description: formData.description.trim(),
          is_active: formData.status === 'true',
        };

        let response: any;
        if (!edit) {
          response = await ProductDivisionService.add(payload);
        } else if (id) {
          response = await ProductDivisionService.edit(id, payload);
        }

        if (response.success !== false) {
          toast.success(
            (
              <Toast
                message={
                  edit
                    ? t('productDivisions.addEdit.pageTexts.form.editSuccess')
                    : t('productDivisions.addEdit.pageTexts.form.addSuccess')
                }
              />
            ) as any,
            { icon: false }
          );

          if (edit) {
            navigate(Modules.products + AuthRoutes.productDivisions);
          } else if (action === '2') {
            window.scrollTo({ top: 0, behavior: 'smooth' });
            const input = document.getElementById(VALIDATION_FIELD_MAPPING.name);
            input && input.focus();
            setFormData(initialFormData);
            setValidation(initialValidation);
          } else {
            navigate(`${Modules.products}${AuthRoutes.productDivisions}`, { replace: true });
          }
        }

        if (action === '1') setLoading(false);
        else setActionTwoLoading(false);
      } catch (err: any) {
        if (action === '1') setLoading(false);
        else setActionTwoLoading(false);
      }
    },
    [id, edit, formData, navigate, t]
  );

  const handleKeyDown = useCallback(
    (e: any, name?: string) => {
      if (e.key === 'Enter') handleSubmit('1');
    },
    [handleSubmit]
  );

  const handleCancel = () => {
    navigate(Modules.products + AuthRoutes.productDivisions);
  };

  let formContent;

  if (getContentLoading) {
    formContent = (
      <div className="card-header text-center">
        <Spinner />
      </div>
    );
  } else {
    formContent = (
      <DivisionDetailsTab
        edit={edit}
        view={view}
        formData={formData}
        validation={validation}
        onKeyDown={handleKeyDown}
        onInputBlur={handleInputBlur}
        onInputChange={handleInputChange}
      />
    );
  }

  return (
    <>
      <Header id={id} view={view} edit={edit} />

      <div className="card mb-6">
        <h5 className="card-header">
          {!id
            ? t('productDivisions.addEdit.pageTexts.title.addTitle')
            : view
              ? t('productDivisions.addEdit.pageTexts.title.viewTitle')
              : t('productDivisions.addEdit.pageTexts.title.editTitle')}
        </h5>
        <hr className="m-0"></hr>

        {formContent}

        <hr className="m-0"></hr>
        <Buttons
          actionTwoLoading={actionTwoLoading}
          loading={loading}
          view={view}
          edit={edit}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
        />
      </div>
    </>
  );
};

export default ProductDivisionAddEditView;
